import { Component, OnInit, OnDestroy } from "@angular/core";
import { Router, ActivatedRoute, Params } from "@angular/router";
import { BaseEditComponent } from "../../core/common/base-component";
import { FormUtil } from "../../core/form.util";
import { ReferralsService } from "../referrals.service";
import { CouponsService } from "../../coupons/services/coupons.service";
import {
  Plan,
  ReferralProgram,
  ReferralReward,
  ReferralRewardRequest,
  ReferralRewardTarget,
  ReferralRewardType,
} from "../referrals.interfaces";
import {
  Coupon,
  CreateCouponRequest,
  ImportCouponRequest,
} from "../../coupons/services/coupons.interfaces";
const alertify = require("alertify.js");
import { Title } from '@angular/platform-browser';
import { capitalizeOnlyFirstLetter } from "../../core/common/capitalize";
import { SubscriptionPlansService } from "../../subscription-plans/services/subscription-plans.service";
@Component({
  selector: "ch-rewards-edit",
  templateUrl: "./rewards-edit.component.html",
  styleUrls: ["./rewards-edit.component.css"],
})
export class ReferralRewardsEditComponent
  extends BaseEditComponent
  implements OnInit, OnDestroy {
  public referralProgramId: string;
  public referralRewardId: string;
  public referralProgramData: ReferralProgram = {
    id: "",
    name: "",
    createdAt: "",
    archivedAt: "",
    deletedAt: "",
  };
  public referralRewardData: ReferralReward = {
    id: "",
    referralProgramId: this.referralProgramId,
    target: ReferralRewardTarget.REFEREE,
    couponId: "",
    planId: null,
    type: ReferralRewardType.MILESTONE,
    autoRedeem: false,
    referralsCount: 1,
    maxRedemptionsPerMember: 1,
    createdAt: "",
    deletedAt: "",
  };
  public existingCoupon = true;
  public importCoupon = false;
  public newCouponData: {
    name: string;
  } & Partial<{
    percentOff: number;
    durationInMonths: number;
    stripeId: string;
  }> = {
    name: "",
  };
  public coupons: Coupon[] = [];
  public plans: Plan[] = [];
  public referralRewardTargetOptions = [
    { value: ReferralRewardTarget.REFEREE, label: "Referee" },
    { value: ReferralRewardTarget.REFERRER, label: "Referrer" },
  ];
  public referralRewardTypeOptions = [
    { value: ReferralRewardType.MILESTONE, label: "Milestone" },
    { value: ReferralRewardType.RECURRING, label: "Recurring" },
  ];
  public errors: any = {};
  public breadcrumb = ["Referral Rewards"];

  constructor(
    private router: Router,
    private referralsService: ReferralsService,
    private couponsService: CouponsService,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private subscriptionPlansService: SubscriptionPlansService,
  ) {
    super();
  }

  ngOnInit() {
    alertify.logPosition("bottom right");
    this.loadData();
    this.loadCoupons();
    this.loadPlans();
  }

  ngOnDestroy(): void {
    this.titleService.setTitle(`CODE Hospitality CMS`);
  }

  public loadData() {
    this.activatedRoute.queryParamMap
      .map((params: Params) => params.params)
      .subscribe((params) => {
        this.referralProgramId = params.referralProgramId;
        this.referralsService
          .getReferralProgramById(this.referralProgramId)
          .subscribe((data) => {
            this.referralProgramData = data.json();
            this.referralRewardId = params.id;
            this.referralRewardData.target = this.getRewardTarget(params.target);
            if (this.referralRewardId) {
              this.breadcrumb.push("Edit");
              this.loading = true;
              this.referralsService
                .getReferralRewardById(
                  this.referralProgramId,
                  this.referralRewardId
                )
                .subscribe(
                  (data) => {
                    this.referralRewardData = data.json();
                    this.referralRewardData.planId = this.referralRewardData.planId || "";
                    this.titleService.setTitle(`${capitalizeOnlyFirstLetter(this.referralRewardData.target)} Reward | ${this.referralProgramData.name} | CODE Hospitality CMS`);
                    this.loading = false;
                  },
                  (err) => {
                    this.loading = false;
                    this.alertError(err);
                    this.cancel();
                  }
                );
            } else {
              this.breadcrumb.push("Add");
              this.titleService.setTitle(`New ${capitalizeOnlyFirstLetter(this.referralRewardData.target)} Reward | ${this.referralProgramData.name} | CODE Hospitality CMS`);
            }
          });
      });
  }

  private getRewardTarget(target: string | ReferralRewardTarget): ReferralRewardTarget {
    if (
      target === ReferralRewardTarget.REFERRER ||
      target === ReferralRewardTarget.REFEREE
    ) {
      return target;
    }

    return this.referralRewardData.target || ReferralRewardTarget.REFEREE;
  }

  public loadCoupons() {
    this.loading = true;
    this.couponsService.getAllCoupons().subscribe(
      (coupons) => {
        this.coupons = coupons.json().filter((c) => c.durationInMonths <= 12);
        this.loading = false;
      },
      (err) => {
        this.loading = false;
        this.alertError(err);
      },
    );
  }

  public loadPlans() {
    this.loading = true;
    this.subscriptionPlansService.getAllSubscriptionPlans().subscribe(
      (plans) => {
        this.plans = plans.json();
      },
    );
  }

  get rafDefaultPlans() {
    return this.plans.filter((plan) => plan.isRafDefault);
  }

  get nonRafDefaultPlans() {
    return this.plans.filter((plan) => !plan.isRafDefault);
  }

  public save(form) {
    if (
      !FormUtil.isValid(form) ||
      this.referralRewardData.referralsCount < 1 ||
      (!this.existingCoupon &&
        !this.importCoupon &&
        this.newCouponData.durationInMonths > 12) ||
      (this.referralRewardData.maxRedemptionsPerMember &&
        this.referralRewardData.maxRedemptionsPerMember < 1)
    ) {
      alertify.error("Please fill the fields correctly.");
      return;
    }

    if (this.existingCoupon) {
      this.saveReward();
    } else {
      let couponRequest: CreateCouponRequest | ImportCouponRequest;
      if (this.importCoupon) {
        couponRequest = {
          name: this.newCouponData.name,
          stripeId: this.newCouponData.stripeId,
        };
      } else {
        couponRequest = {
          name: this.newCouponData.name,
          percentOff: this.newCouponData.percentOff,
          durationInMonths: this.newCouponData.durationInMonths,
        };
      }

      this.loading = true;
      this.couponsService.addCoupon(couponRequest).subscribe(
        (coupon) => {
          this.referralRewardData.couponId = coupon.json().id;
          this.loading = false;
          this.saveReward();
        },
        (err) => {
          this.loading = false;
          this.alertError(err);
        },
      );
    }
  }

  private saveReward() {
    const target = this.getRewardTarget(this.referralRewardData.target);

    const request: ReferralRewardRequest = {
      target,
      couponId: this.referralRewardData.couponId,
      planId: this.referralRewardData.planId,
      type: this.referralRewardData.type,
      autoRedeem:
      target === ReferralRewardTarget.REFEREE
        ? true
        : this.referralRewardData.autoRedeem,
      referralsCount: this.referralRewardData.referralsCount,
      maxRedemptionsPerMember: this.referralRewardData.maxRedemptionsPerMember,
    };

    this.loading = true;
    if (this.referralRewardId) {
      this.referralsService
        .updateReferralReward(
        this.referralProgramId,
        this.referralRewardId,
        request,
      )
        .subscribe(
        (_) => {
          this.loading = false;
          this.cancel();
        },
        (err) => {
          this.loading = false;
          this.alertError(err);
        },
      );
    } else {
      this.referralsService
        .createReferralReward(this.referralProgramId, request)
        .subscribe(
        (_) => {
          this.loading = false;
          this.cancel();
        },
        (err) => {
          this.loading = false;
          if (err.status === 400) {
            alertify.error(err.error);
          } else {
            this.alertError(err);
          }
        },
      );
    }
  }

  public delete() {
    this.loading = true;
    this.referralsService
      .deleteReferralReward(this.referralProgramId, this.referralRewardId)
      .subscribe(
      (res) => {
        alertify.success("Referral reward deleted successfully.");
        this.cancel();
      },
      (err) => {
        this.alertError(err);
        this.loading = false;
      },
    );
  }

  public cancel() {
    this.router.navigate(["/admin/referral-programs/edit"], {
      queryParams: {
        id: this.referralProgramId,
      },
    });
  }

  private alertError(error: any) {
    if (!error.error) {
      alertify.error(`There was a ${error.status} error with the request.`);
    } else {
      alertify.error(
        `There was a ${error.status} error with message '${error.error}'.`,
      );
    }
  }
}



// WEBPACK FOOTER //
// /home/runner/_work/monorepo/monorepo/admin-portal-old/src/app/referrals/rewards/rewards-edit.component.ts